import style from "./mainArticle1.module.scss";
import faces from "../../../assets/images/main/article1/section2/face_group.png";
import facesMobile from "../../../assets/images/main/article1/section2/face_group_mobile.png";
import children from "../../../assets/images/main/article1/section2/children.png";
import childrenMobile from "../../../assets/images/main/article1/section2/children_mobile.png";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMainScrolling, useScrollingEvent, useUserAgent } from "../../../hooks/hooks";
import { StrongTextStyle } from "../../common/StyledCommonComponents";
import { SlidingPartPassingStatus } from "../../../hooks/main_scrolling/MainScrollingContext";
import { Trans, useTranslation } from "react-i18next";

enum PassingStatus {
  IS_BEFORE_PASSING = "IS_BEFORE_PASSING",
  IS_PASSING = "IS_PASSING",
  IS_DONE = "IS_DONE",
}

const ARCH_HEIGHT_ON_PRESS_SECTION = 110;
const WAVE_HEIGHT_ON_HEADER = 10;

export function Article1Section2() {
  const { t } = useTranslation(["parentsWish"]);
  const { isDesktop } = useUserAgent();
  const { scrollY, stopScrollYAndFix, restartScroll } = useScrollingEvent();
  const { setSlidingPartPassingStatus, headerHeight } = useMainScrolling();
  const contentRef = useRef<HTMLDivElement>(null);
  const [passingStatus, setPassingStatus] = useState<PassingStatus>(PassingStatus.IS_BEFORE_PASSING);
  const [translateXValue, setTranslateXValue] = useState(0);
  const [showChildren, setShowChildren] = useState(false);
  const [characterScale, setCharacterScale] = useState(0);
  const contentRect: DOMRect | undefined = useMemo(
    () => contentRef.current?.getBoundingClientRect(),
    // eslint-disable-next-line
    [contentRef.current]
  );
  const translateXMax = useMemo(() => window.innerWidth - (contentRect?.width ?? 0), [contentRect]);

  const isPassing = useMemo(() => passingStatus === PassingStatus.IS_PASSING, [passingStatus]);

  const archHeight = useMemo(() => (isDesktop ? ARCH_HEIGHT_ON_PRESS_SECTION : 0), [isDesktop]);

  useEffect(() => {
    const halfWidth = isDesktop ? window.innerWidth / 2 : window.innerWidth * 0.7;
    const startPoint = translateXMax + halfWidth;

    let ratio = (startPoint - translateXValue) / (halfWidth - 100);
    if (ratio < 0) ratio = 0;
    if (1 < ratio) ratio = 1;
    setCharacterScale(ratio);
  }, [translateXValue, translateXMax, isDesktop]);

  const sectionRef = useRef<HTMLElement>(null);
  const [sectionTop, setSectionTop] = useState(0);
  useEffect(() => {
    if (scrollY === 0 && sectionRef.current) {
      const { top } = sectionRef.current.getBoundingClientRect();
      setSectionTop(top + archHeight); // 1804
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollY, sectionRef.current, archHeight]);

  useEffect(() => {
    if (sectionTop) {
      let nextValue = (scrollY - sectionTop + (headerHeight - WAVE_HEIGHT_ON_HEADER)) * -1;
      switch (true) {
        case 0 < nextValue:
          nextValue = 0;
          break;
        case nextValue < translateXMax:
          nextValue = translateXMax;
          if (!showChildren) {
            setShowChildren(true);
          }
          break;
        default:
          setShowChildren(false);
          break;
      }

      setTranslateXValue(nextValue);
    }
  }, [
    scrollY,
    sectionTop,
    translateXMax,
    headerHeight,
    showChildren,
    setShowChildren,
    stopScrollYAndFix,
    restartScroll,
    isDesktop,
  ]);

  useEffect(() => {
    const xValue = Math.abs(translateXValue);
    const maxValue = Math.abs(translateXMax);
    switch (true) {
      case xValue === 0:
        setPassingStatus(PassingStatus.IS_BEFORE_PASSING);
        break;
      case 0 < xValue && xValue < maxValue:
        setPassingStatus(PassingStatus.IS_PASSING);
        break;
      case xValue === maxValue:
        setPassingStatus(PassingStatus.IS_DONE);
        break;
      default:
        setPassingStatus(PassingStatus.IS_BEFORE_PASSING);
        break;
    }
  }, [translateXValue, translateXMax]);

  useEffect(() => {
    if (passingStatus === PassingStatus.IS_DONE) {
      setSlidingPartPassingStatus(SlidingPartPassingStatus.IS_AFTER_PASSING);
    } else {
      setSlidingPartPassingStatus(SlidingPartPassingStatus.IS_BEFORE_PASSING);
    }
    // eslint-disable-next-line
  }, [passingStatus]);

  const SpaceComponent = useCallback(() => <>{isDesktop ? <> </> : <br />}</>, [isDesktop]);

  return (
    <section
      className={`${style.section2}
    ${isPassing ? style.isPassing : ""}
    ${passingStatus === PassingStatus.IS_DONE ? style.isDone : ""}
    ${passingStatus === PassingStatus.IS_BEFORE_PASSING ? style.isBefore : ""}
    `}
      ref={sectionRef}
    >
      <h2 className={"blind"}>{t("section2.heading2")}</h2>
      {isPassing && <div className={style.whiteBg}></div>}
      <div
        className={style.content}
        style={{
          transform: `translate3d(${translateXValue}px, 0, 0)`,
          top: `${isPassing ? `${headerHeight - WAVE_HEIGHT_ON_HEADER - archHeight}px` : ""}`,
        }}
        ref={contentRef}
      >
        <div className={style.descWrapper} style={{ left: `${translateXValue * -1}px` }}>
          <p className={style.desc}>
            <Trans t={t} i18nKey={"section2.description"} components={[<StrongTextStyle />, <SpaceComponent />]} />
          </p>
        </div>
        <div className={style.bottomVillagePattern} />
        <div className={style.faceGroup}>
          <img src={isDesktop ? faces : facesMobile} alt={"faces"} />
        </div>
        <div className={style.cloud}></div>
        <div
          className={`${style.childrenCharacterWrapper} ${isPassing ? style.isPassing : ""}`}
          style={{ left: isPassing ? `${translateXValue * -1}px` : "" }}
        >
          <img
            className={`${style.childrenImage} ${showChildren ? style.show : ""}`}
            src={isDesktop ? children : childrenMobile}
            alt={"children"}
            style={{ transform: `scale(${characterScale})` }}
          />
        </div>
      </div>
    </section>
  );
}
